import { CoreStore } from '../../../../../core/store/core.store';
import { getLoginJWTToken, getLoginUser } from '../../../../../modules/login/store/login.selectors';
import {
  filterMethodsByTypes,
  hasSecurityTokenExpired,
  isSetupMethodType,
  methodTypeToIconMap,
  TWO_FACTOR_CODE_LENGTH,
  TWO_FACTOR_SETUP_METHODS_BY_PRIORITY,
  TwoFAType
} from '../../domain/two-factor';
import { TwoFactorSetupService } from '../../services/two-factor-setup.service';
import { TwoFactorError, TwoFactorService, TwoFAResult } from '../../services/two-factor.service';
import { getVerificationErrorMessage } from '../two-factor-method-setup-verify/two-factor-method-setup-verify.component';
import { createUnlockRouteState } from '../two-factor-unlock/two-factor-unlock.component';
import { verificationCodeValidator } from '../two-factor-verification-code/two-factor-verification-code.component';
import { Location } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { getAppAbsoluteURL } from '@ppl/core';
import { I18nService } from '@ppl/i18n';
import { PplDialogService } from '@ppl/ui/dialog';
import { TFormBuilder } from '@ppl/ui/form';
import { wrapAPICall } from '@ppl/utils/api.utils';
import { sortByPriority } from '@ppl/utils/array.utils';
import { DialogResult } from '@ppl/utils/dialog.utils';
import { select } from '@ppl/utils/selector.utils';
import { unsubscribe } from '@ppl/utils/subscription.utils';
import { assertNever } from '@ppl/utils/typescript.utils';
import moment from 'moment';
import { createSelector } from 'reselect';
import {
  BehaviorSubject,
  combineLatest,
  concat,
  EMPTY,
  of,
  Subject,
  throwError
} from 'rxjs';
import {
  delay,
  map,
  switchMap,
  tap
} from 'rxjs/operators';
import type {
  OnDestroy,
  OnInit
} from '@angular/core';
import type { TFormGroup } from '@ppl/ui/form';
import type {
  Observable,
  Subscription
} from 'rxjs';
import type { LoginUser } from '../../../../../modules/login/store/login.reducer';
import type {
  TwoFactorSecurityTokenData,
  TwoFactorSetupMethodType, TwoFAMethod
} from '../../domain/two-factor';
import type {
  TwoFactorErrorInfo
} from '../../services/two-factor.service';
import type { TwoFactorUnlockNavigateBackRouteState } from '../two-factor-unlock/two-factor-unlock.component';

@Component({
    selector: 'ppl-two-factor-screen',
    templateUrl: './two-factor-screen.component.html',
    styleUrls: ['./two-factor-screen.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class TwoFactorScreenComponent implements OnInit, OnDestroy {

  loading$ = new BehaviorSubject<boolean>(false);
  setuping$ = new BehaviorSubject<boolean>(false);

  state$ = new BehaviorSubject<TwoFactorScreenState>({
    mode: undefined,
    currentMethodId: null,
    methods: [],
    currentSetupMethodType: null,
    setupMethodTypes: [],
    error: null,
    verification: null,
    securityTokenData: null
  });
  user$ = this.store.select(getLoginUser);

  optionsForm: TFormGroup<TwoFactorScreenOptionsFormData>;
  optionsMethods$: Observable<TwoFAMethod[]>;

  verifyForm: TFormGroup<TwoFactorScreenVerifyFormData>;
  verification$: Observable<TwoFactorScreenVerification | null>;

  currentMethod$: Observable<TwoFAMethod>;
  currentMethodValue$: Observable<string>;
  currentMethodName$: Observable<string>;
  currentSetupMethodType$: Observable<TwoFactorSetupMethodType>;
  setupMethodTypes$: Observable<TwoFactorSetupMethodType[]>;

  error$: Observable<TwoFactorErrorInfo | null>;
  mode$: Observable<TwoFactorMode>;
  title$: Observable<string>;
  description$: Observable<string>;

  rememberDisabledForTypes: TwoFAType[] = [
    TwoFAType.BACKUP_CODES,
    TwoFAType.EMAIL_MS_IDENTITY_CONFIRMATION
  ];
  setupVerifyStatusPollingForTypes: TwoFAType[] = [
    TwoFAType.EMAIL,
    TwoFAType.EMAIL_MS_IDENTITY_CONFIRMATION
  ];

  TwoFactorMode = TwoFactorMode;
  TwoFAType = TwoFAType;
  TwoFactorError = TwoFactorError;
  methodTypeToIconMap = methodTypeToIconMap;
  TWO_FACTOR_CODE_LENGTH = TWO_FACTOR_CODE_LENGTH;

  verifyResendCode$ = new BehaviorSubject<{ method: TwoFAMethod, created: moment.Moment }>(null);
  verifyResendCodeSend$ = new Subject();
  verifyResendToggleIcon$ = this.verifyResendCodeSend$.pipe(
    switchMap(() => {
      return concat(of(true), of(false).pipe(delay(HIDE_ICON_AFTER_TIME)));
    })
  );
  verifyResendCodeDisabled$ = this.verifyResendCode$.pipe(
    switchMap(resendRequest => {
      if (resendRequest) {
        const now = moment.utc();
        const disabledUntil = moment(resendRequest.created).add(RESEND_CODE_RATE_LIMIT, 'milliseconds');
        return disabledUntil.isAfter(now)
          ? concat(
            of(true),
            of(false).pipe(delay(disabledUntil.diff(now)))
          )
          : of(false);
      } else {
        return of(false);
      }
    })
  );
  verifyResendCodeVisible$: Observable<boolean>;
  verifyChallengeStatusSubscription: Subscription;

  get state() {
    return this.state$.value;
  }

  constructor(
    private store: CoreStore,
    private i18nService: I18nService,
    private fb: TFormBuilder,
    private twoFactorService: TwoFactorService,
    private twoFactorSetupService: TwoFactorSetupService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private dialogService: PplDialogService,
    private location: Location
  ) {
    this.optionsForm = this.fb.group<TwoFactorScreenOptionsFormData>({
      methodId: [null, Validators.required]
    });
    this.verifyForm = this.fb.group<TwoFactorScreenVerifyFormData>({
      code: [[], [verificationCodeValidator(this.i18nService)]],
      remember: [true]
    });
    this.verification$ = this.state$.pipe(map(select(getVerification)));

    this.error$ = this.state$.pipe(map(select(createSelector(
      state => state.error,
      error => error
    ))));

    this.currentMethod$ = this.state$.pipe(map(select(getCurrentMethod)));
    this.mode$ = this.state$.pipe(map(select(createSelector(state => state.mode, mode => mode))));
    this.title$ = this.mode$.pipe(map(mode => this.getTitle(mode)));
    this.description$ = combineLatest([
      this.mode$,
      this.currentMethod$
    ]).pipe(
      map(([mode, method]) => {
        return this.getDescription({ mode, method });
      })
    );

    this.verifyResendCodeVisible$ = this.currentMethod$.pipe(
      map(method => {
        return [TwoFAType.EMAIL, TwoFAType.SMS].includes(method.type);
      })
    );

    this.currentMethodValue$ = this.currentMethod$.pipe(
      map(method => {
        return method && method.type !== TwoFAType.BACKUP_CODES && method.value && method.value.length
          ? method.value[0]
          : '';
      })
    );
    this.currentMethodName$ = this.currentMethod$.pipe(
      map(method => {
        return method
          ? method.display_name
          : '';
      })
    );

    this.optionsMethods$ = this.state$.pipe(map(select(createSelector(
      state => state.methods,
      methods => {
        return methods;
      }
    ))));

    this.setupMethodTypes$ = this.state$.pipe(map(select(createSelector(
      getCurrentSetupMethodType,
      state => state.setupMethodTypes,
      (currentType, types) => {
        return types.filter(type => type !== currentType);
      }
    ))));
    this.currentSetupMethodType$ = this.state$.pipe(map(select(getCurrentSetupMethodType)));

    const navigation = this.router.getCurrentNavigation();
    const unlockBackState = navigation?.extras?.state as TwoFactorUnlockNavigateBackRouteState<TwoFactorScreenUnlockNavigateBackState>;
    if (unlockBackState) {
      const { security, navigateBackState } = unlockBackState.unlockData;
      this.updateState({ securityTokenData: security });
      if (navigateBackState) {
        switch (navigateBackState.operation) {
          case 'setup':
            this.updateState({ currentSetupMethodType: navigateBackState.setupMethodType });
            break;
        }
      }
    }
  }

  ngOnInit() {
    const loginUser = this.store.get(getLoginUser);
    const initState = this.getInitState(this.state, loginUser);
    this.updateState(initState);
    this.setupVerifyStatusPolling();

    if (initState.mode === TwoFactorMode.VERIFY) {
      this.startVerification(initState.currentMethodId).subscribe();
    }

    if (initState.mode === TwoFactorMode.SETUP && this.hasSecurityToken(this.state.securityTokenData)) {
      this.setupMethod({
        email: loginUser.email,
        securityToken: this.state.securityTokenData.token,
        type: initState.currentSetupMethodType
      });
    }
  }

  ngOnDestroy() {
    unsubscribe(this.verifyChallengeStatusSubscription);
  }

  onBackToLogin() {
    this.redirectToLogin();
  }

  onBackButtonClick() {
    const user = this.store.get(getLoginUser);
    if (user.logged_in) {
      this.location.back();
    } else {
      this.redirectToLogin();
    }
  }

  onSetupMethodTypeChange(type: TwoFactorSetupMethodType) {
    this.updateState({ currentSetupMethodType: type });
  }

  onSetupMethodType(type: TwoFactorSetupMethodType) {
    if (type) {
      const loginUser = this.store.get(getLoginUser);
      const securityTokenData = this.state.securityTokenData;
      if (this.hasSecurityToken(securityTokenData)) {
        this.setupMethod({ type, email: loginUser.email, securityToken: securityTokenData.token });
      } else {
        const unlockState = createUnlockRouteState<TwoFactorScreenUnlockNavigateBackState>({
          // NOTE(mike): Here it's a number but on IdentityProviderEntity it's of type string.
          idpId: typeof loginUser.idp_id === 'number'
            ? String(loginUser.idp_id)
            : null,
          email: loginUser.email,
          navigateBackUrl: this.router.url,
          navigateBackState: {
            operation: 'setup',
            setupMethodType: type
          }
        });
        this.router.navigate(
          ['../unlock'],
          { relativeTo: this.activatedRoute, state: unlockState }
        );
      }
    }
  }

  setupMethod(data: { email: string, type: TwoFactorSetupMethodType, securityToken: string }) {
    this.setuping$.next(true);
    this.twoFactorSetupService.setupMethod(data).subscribe(response => {
      if (response?.result === DialogResult.SUCCESS) {
        this.loading$.next(true);
        window.location.reload();
      }
      this.setuping$.next(false);
    });
  }

  onOptionsBackButtonClick() {
    this.updateState({ mode: TwoFactorMode.VERIFY });
  }

  onOptionsSetTwoFactorMethod() {
    const { methodId } = this.optionsForm.value;
    this.updateState({ mode: TwoFactorMode.VERIFY, currentMethodId: methodId });
    this.startVerification(methodId).subscribe();
  }

  onVerifyCodeClick() {
    if (this.verifyForm.valid) {
      const { code, remember } = this.verifyForm.value;
      const { jwt, methodId } = getVerification(this.state$.value);
      const method = getMethodById(methodId)(this.state$.value);
      const verifyData = {
        code: code.join(''),
        jwt,
        methodId,
        ...(method.type === TwoFAType.BACKUP_CODES ? {} : { remember })
      };

      this.updateState({ error: null });
      this.loading$.next(true);
      this.twoFactorService.verifyChallenge(verifyData).pipe(
        this.twoFactorService.handleError({
          errorHandlerFn: (error, originalError) => {
            this.loading$.next(false);
            if (error.error === TwoFactorError.NOT_FOUND) {
              return this.dialogService.alert({
                text: this.i18nService.translate('Selected_2-step_verification_error'),
                description: this.i18nService.translate(
                  '2-step_verification_is_disabled,_click_Ok_to_continue'
                ),
                actionLabel: this.i18nService.translate('Ok')
              }).pipe(switchMap(() => {
                this.redirectTo('/logout');
                return EMPTY;
              }));
            } else {
              this.updateState({ error });
              return throwError(originalError);
            }
          }
        })
      ).subscribe({
        next: response => {
          switch (response.result) {
            case TwoFAResult.FINISHED:
              const method = getMethodById(methodId)(this.state$.value);
              const confirm$ = this.isBackupCodeMethod(method) && this.getRemainingBackupCodeCount(method) === 1
                ? this.dialogService.alert({
                  text: this.i18nService.translate('TwoFactorLastBackupCodeUsed'),
                  actionLabel: this.i18nService.translate('Ok')
                })
                : of(true);
              confirm$.subscribe(() => {
                window.location.href = getAppAbsoluteURL(response.redirect_url);
              });
              break;
            case TwoFAResult.EXPIRED:
            case TwoFAResult.FAILED: {
              const codeFormControl = this.verifyForm.get('code');
              codeFormControl.setValue([]);
              codeFormControl.setErrors({
                custom: getVerificationErrorMessage(this.i18nService, response.result)
              });
              this.loading$.next(false);
            } break;
            case TwoFAResult.IN_PROGRESS: // Returned when you would send empty value.
              this.loading$.next(false);
              break;
            default:
              assertNever(response.result);
          }
        }
      });
    } else {
      this.verifyForm.markAllAsTouched();
    }
  }

  onVerifyResendCode() {
    const method = getCurrentMethod(this.state);
    this.startVerification(method.id).subscribe({
      next: () => {
        this.verifyForm.get('code').setValue([]);
        this.verifyForm.markAsUntouched();
        this.verifyForm.markAsPristine();
        this.verifyResendCode$.next({ method, created: moment.utc() });
        this.verifyResendCodeSend$.next(null);
      }
    });
  }

  onVerifyMoreOptionClick() {
    const currentMethod = getCurrentMethod(this.state$.value);
    this.optionsForm.setValue({ methodId: currentMethod.id });
    this.updateState({
      mode: TwoFactorMode.OPTIONS,
      error: null
    });
  }

  updateState(state: Partial<TwoFactorScreenState>) {
    this.state$.next({ ...this.state$.value, ...state });
  }

  getInitState(currentState: TwoFactorScreenState, loginUser: LoginUser): TwoFactorScreenState {
    const allEnabledMethods = loginUser.two_fa_methods.filter(method => method.enabled);
    const backupCodeMethods = filterMethodsByTypes(allEnabledMethods, [TwoFAType.BACKUP_CODES]);
    const relevantMethods = allEnabledMethods
      .filter(method => method.type !== TwoFAType.BACKUP_CODES);

    if (loginUser.required_two_fa_methods && loginUser.required_two_fa_methods.length) {
      // NOTE(mike,2fa): `required_two_fa_methods` should not contain backup codes.
      const requiredMethods = filterMethodsByTypes(relevantMethods, loginUser.required_two_fa_methods);
      if (requiredMethods.length) {
        return this.createVerifyInitState(currentState, requiredMethods);
      } else {
        return this.createSetupInitState(currentState, loginUser.required_two_fa_methods);
      }
    } else {
      if (relevantMethods.length) {
        return this.createVerifyInitState(currentState, [...relevantMethods, ...backupCodeMethods]);
      } else {
        return this.createSetupInitState(currentState, TWO_FACTOR_SETUP_METHODS_BY_PRIORITY);
      }
    }
  }

  setupVerifyStatusPolling() {
    this.verifyChallengeStatusSubscription = this.state$.pipe(
      map(state => {
        return state.verification
          ? { method: getMethodById(state.verification.methodId)(state), jwt: state.verification.jwt }
          : null;
      }),
      switchMap(data => {
        if (data && this.setupVerifyStatusPollingForTypes.includes(data.method.type)) {
          return this.twoFactorService.pollStatus(
            this.twoFactorService.verifyChallengeStatus({ jwt: data.jwt, methodId: data.method.id })
          );
        } else {
          return EMPTY;
        }
      })
    ).subscribe({
      next: ([response, isDone]) => {
        if (isDone) {
          switch (response.result) {
            case TwoFAResult.FINISHED:
              this.loading$.next(true);
              this.redirectTo(response.redirect_url);
              break;
            default:
              break;
          }
        }
      }
    });
  }

  hasSecurityToken(data: TwoFactorSecurityTokenData | null) {
    return Boolean(data && !hasSecurityTokenExpired(data));
  }

  getDefaultMethod(methods: TwoFAMethod[]): TwoFAMethod | null {
    return methods.find(method => method.default);
  }

  isBackupCodeMethod(method: TwoFAMethod): boolean {
    return method.type === TwoFAType.BACKUP_CODES;
  }

  getRemainingBackupCodeCount(method: TwoFAMethod): number {
    // NOTE(mike): `value` is a list of `null` or `***`. `null` for used code and `***` for still not used`.
    // e.g. `[null, '***', null, null, '***', ...]
    return method.value.filter(value => Boolean(value)).length;
  }

  isLastBackupCode(method: TwoFAMethod): boolean {
    return this.isBackupCodeMethod(method)
      ? this.getRemainingBackupCodeCount(method) === 1
      : false;
  }

  createVerifyInitState(
    currentState: TwoFactorScreenState,
    methods: TwoFAMethod[]
  ): TwoFactorScreenState {
    const methodsSortedByPriority = [...methods].sort(sortByPriority(
      TWO_FACTOR_SETUP_METHODS_BY_PRIORITY.map(x => String(x)),
      method => String(method.type)
    ));
    const defaultMethod = this.getDefaultMethod(methodsSortedByPriority) || methodsSortedByPriority[0];
    return {
      mode: TwoFactorMode.VERIFY,
      currentMethodId: defaultMethod.id,
      methods,
      currentSetupMethodType: null,
      setupMethodTypes: [],
      error: null,
      verification: null,
      securityTokenData: currentState.securityTokenData
    };
  }

  createSetupInitState(
    currentState: TwoFactorScreenState,
    setupMethodTypes: TwoFAType[]
  ): TwoFactorScreenState {
    const setupMethodTypesSortedByPriority = setupMethodTypes
      .filter(isSetupMethodType)
      .sort(sortByPriority(
        TWO_FACTOR_SETUP_METHODS_BY_PRIORITY.map(x => String(x)),
        methodType => String(methodType)
      ));
    const defaultMethodType = currentState.currentSetupMethodType && setupMethodTypesSortedByPriority.includes(currentState.currentSetupMethodType)
      ? currentState.currentSetupMethodType
      : setupMethodTypesSortedByPriority[0];
    return {
      mode: TwoFactorMode.SETUP,
      currentMethodId: null,
      methods: [],
      currentSetupMethodType: defaultMethodType,
      setupMethodTypes: setupMethodTypesSortedByPriority,
      error: null,
      verification: null,
      securityTokenData: currentState.securityTokenData
    };
  }

  getTitle(mode: TwoFactorMode) {
    switch (mode) {
      case TwoFactorMode.VERIFY:
      case TwoFactorMode.SETUP:
        return this.i18nService.translate('2-Step_Verification');
      case TwoFactorMode.OPTIONS:
        return this.i18nService.translate('More_Options');
      default:
        return '';
    }
  }

  getDescription(state: { mode: TwoFactorMode, method: TwoFAMethod | null }) {
    switch (state.mode) {
      case TwoFactorMode.VERIFY:
        return this.twoFactorService.getMethodName(state.method.type);
      case TwoFactorMode.SETUP:
        return this.i18nService.translate('');
      case TwoFactorMode.OPTIONS:
        return this.i18nService.translate('Available_2-Step_verification_methods');
      default:
        return '';
    }
  }

  getRetryAfterMessage(error: TwoFactorErrorInfo) {
    return this.twoFactorService.getRetryAfterMessage(error.retryAfter);
  }

  startVerification(methodId: number) {
    const jwt = this.store.get(getLoginJWTToken);
    const method = getMethodById(methodId)(this.state$.value);

    this.updateState({ error: null, verification: null });
    if (this.isBackupCodeMethod(method) && !this.getRemainingBackupCodeCount(method)) {
      this.dialogService.alert({
        text: '',
        description: this.i18nService.translate(
          'TwoFactorAllBackupCodesUsed',
          { support_email: '<a href="mailto:support@pipelinersales.com">support@pipelinersales.com</a>' }
        ),
        actionLabel: this.i18nService.translate('Ok')
      });
    }

    return wrapAPICall(
      this.twoFactorService.verifyStart({ jwt, methodId }),
      this.loading$
    ).pipe(
      this.twoFactorService.handleError({
        errorHandlerFn: (error, originalError) => {
          this.updateState({ error });
          if (error.error === TwoFactorError.TOO_MANY_REQUESTS) {
            return EMPTY;
          } else {
            return throwError(originalError);
          }
        }
      }),
      tap({
        next: () => {
          this.updateState({ verification: { methodId, jwt } });
        }
      })
    );
  }

  redirectTo(url: string) {
    this.loading$.next(true);
    window.location.href = getAppAbsoluteURL(url);
  }

  redirectToLogin() {
    this.redirectTo('/login');
  }
}

const getVerification = createSelector(
  (state: TwoFactorScreenState) => state.verification,
  verification => {
    return verification;
  }
);

function getMethodById(id: number) {
  return createSelector(
    (state: TwoFactorScreenState) => state.methods,
    methods => {
      const foundMethod = methods.find(method => method.id === id);
      return foundMethod;
    }
  );
}

const getCurrentMethod = createSelector(
  (state: TwoFactorScreenState) => state.currentMethodId,
  (state: TwoFactorScreenState) => state.methods,
  (currentMethodId, methods) => {
    const foundMethod = methods.find(method => method.id === currentMethodId);
    return foundMethod;
  }
);

const getCurrentSetupMethodType = createSelector(
  (state: TwoFactorScreenState) => state.currentSetupMethodType,
  currentType => currentType
);

const HIDE_ICON_AFTER_TIME = 2000;
const RESEND_CODE_RATE_LIMIT = 60 * 1000;

interface TwoFactorScreenVerification {
  methodId: number;
  jwt: string;
}

interface TwoFactorScreenState {
  mode: TwoFactorMode;
  currentMethodId: number | null;
  methods: TwoFAMethod[];
  currentSetupMethodType: TwoFactorSetupMethodType | null;
  setupMethodTypes: TwoFactorSetupMethodType[];
  error: TwoFactorErrorInfo | null;
  verification: TwoFactorScreenVerification | null;
  securityTokenData: TwoFactorSecurityTokenData | null;
}

enum TwoFactorMode {
  VERIFY = 1,
  SETUP,
  OPTIONS
}

interface TwoFactorScreenOptionsFormData {
  methodId: number;
}

interface TwoFactorScreenVerifyFormData {
  code: string[];
  remember: boolean;
}

interface TwoFactorScreenUnlockNavigateBackState {
  operation: 'setup';
  setupMethodType: TwoFactorSetupMethodType;
}
